import React from 'react';
import { Html } from '@react-three/drei';
import type * as _THREE from 'three';
import { ShotComparisonData, ShotAnimation } from '../types';

interface PhysicsAnnotationsProps {
  shotComparison: ShotComparisonData;
  animation: ShotAnimation;
}

export const PhysicsAnnotations: React.FC<PhysicsAnnotationsProps> = ({
  shotComparison,
  animation
}) => {
  const { actual, optimal, optimization, actualTrajectory, optimalTrajectory } = shotComparison;

  // Helper function to format numbers
  const formatNumber = (value: number, decimals: number = 1) => {
    return value.toFixed(decimals);
  };
  
  // Debug: Log unrealistic values
  if (actual.exitVelocity > 60 || optimal.exitVelocity > 60) {
    console.warn('Unrealistic exit velocity detected:', {
      actual: actual.exitVelocity,
      optimal: optimal.exitVelocity,
      actualKmh: (actual.exitVelocity * 3.6).toFixed(1),
      optimalKmh: (optimal.exitVelocity * 3.6).toFixed(1)
    });
  }

  // Helper function to get color based on comparison
  const getComparisonColor = (actualValue: number, optimalValue: number, higherIsBetter: boolean = true) => {
    const ratio = actualValue / optimalValue;
    if (higherIsBetter) {
      if (ratio >= 0.95) return '#22c55e'; // Green - excellent
      if (ratio >= 0.85) return '#fbbf24'; // Yellow - good
      return '#ef4444'; // Red - needs improvement
    } else {
      if (ratio <= 1.05) return '#22c55e'; // Green - excellent
      if (ratio <= 1.15) return '#fbbf24'; // Yellow - good
      return '#ef4444'; // Red - needs improvement
    }
  };

  return (
    <group>
      {/* Launch Angle Annotation - Bottom Left */}
      <Html
        position={[-8, 3, -5]}
        distanceFactor={10}
        style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '14px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          minWidth: '200px'
        }}
      >
        <div>
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Launch Angle</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Actual:</span>
            <span style={{ 
              color: getComparisonColor(actual.launchAngle, optimal.launchAngle, false)
            }}>
              {formatNumber(actual.launchAngle)}°
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Optimal:</span>
            <span style={{ color: '#22c55e' }}>{formatNumber(optimal.launchAngle)}°</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '2px' }}>
            <span>Difference:</span>
            <span style={{ color: '#fbbf24' }}>
              {optimization.improvements.launchAngleDelta > 0 ? '+' : ''}
              {formatNumber(optimization.improvements.launchAngleDelta)}°
            </span>
          </div>
        </div>
      </Html>

      {/* Exit Velocity Annotation - Top Right */}
      <Html
        position={[8, 5, 2]}
        distanceFactor={10}
        style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '14px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          minWidth: '200px'
        }}
      >
        <div>
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Exit Velocity</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Actual:</span>
            <span style={{ 
              color: getComparisonColor(actual.exitVelocity, optimal.exitVelocity)
            }}>
              {formatNumber(Math.min(55, actual.exitVelocity))} m/s
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Optimal:</span>
            <span style={{ color: '#22c55e' }}>{formatNumber(Math.min(55, optimal.exitVelocity))} m/s</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '2px' }}>
            <span>Difference:</span>
            <span style={{ color: '#fbbf24' }}>
              {optimization.improvements.velocityDelta > 0 ? '+' : ''}
              {formatNumber(optimization.improvements.velocityDelta)} m/s
            </span>
          </div>
        </div>
      </Html>

      {/* Distance Comparison - Top Center */}
      <Html
        position={[0, 10, 10]}
        distanceFactor={12}
        style={{
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '16px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          minWidth: '280px',
          textAlign: 'center'
        }}
      >
        <div>
          <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px' }}>DISTANCE COMPARISON</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>Actual Distance:</span>
            <span style={{ 
              color: getComparisonColor(actualTrajectory.totalDistance, optimalTrajectory.totalDistance),
              fontWeight: 'bold'
            }}>
              {formatNumber(actualTrajectory.totalDistance)} m
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>Optimal Distance:</span>
            <span style={{ color: '#22c55e', fontWeight: 'bold' }}>
              {formatNumber(optimalTrajectory.totalDistance)} m
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            fontSize: '14px', 
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid rgba(255,255,255,0.3)'
          }}>
            <span>Potential Gain:</span>
            <span style={{ 
              color: optimization.potentialDistanceGain > 0 ? '#fbbf24' : '#22c55e',
              fontWeight: 'bold'
            }}>
              +{formatNumber(optimization.potentialDistanceGain)} m
            </span>
          </div>
        </div>
      </Html>

      {/* Power Transfer during Impact */}
      {animation.currentPhase === 'impact' && (
        <Html
          position={[1, 3, -1]}
          distanceFactor={8}
          style={{
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
            pointerEvents: 'none',
            minWidth: '180px'
          }}
        >
          <div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Power Transfer</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Efficiency:</span>
              <span style={{ 
                color: actual.powerTransfer > 0.8 ? '#22c55e' : 
                      actual.powerTransfer > 0.6 ? '#fbbf24' : '#ef4444',
                fontWeight: 'bold'
              }}>
                {formatNumber(actual.powerTransfer * 100)}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
              <span>Sweet Spot:</span>
              <span style={{ 
                color: actual.sweetSpotDistance < 0.02 ? '#22c55e' : '#ef4444'
              }}>
                {actual.sweetSpotDistance < 0.02 ? 'HIT' : 'MISSED'}
              </span>
            </div>
          </div>
        </Html>
      )}

      {/* Flight Metrics during Trajectory */}
      {animation.currentPhase === 'postImpact' && (
        <Html
          position={[4, 6, 20]}
          distanceFactor={15}
          style={{
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
            pointerEvents: 'none',
            minWidth: '200px'
          }}
        >
          <div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Flight Metrics</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Max Height:</span>
              <span>{formatNumber(actualTrajectory.maxHeight)} m</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Flight Time:</span>
              <span>{formatNumber(actualTrajectory.flightTime)} s</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Landing Angle:</span>
              <span>{formatNumber(actualTrajectory.landingAngle)}°</span>
            </div>
          </div>
        </Html>
      )}

      {/* Shot Quality Indicator - Top Right Corner */}
      <Html
        position={[10, 8, -5]}
        distanceFactor={10}
        style={{
          background: actual.quality === 'excellent' ? 'rgba(34, 197, 94, 0.9)' :
                      actual.quality === 'good' ? 'rgba(251, 191, 36, 0.9)' :
                      actual.quality === 'fair' ? 'rgba(249, 115, 22, 0.9)' :
                      'rgba(239, 68, 68, 0.9)',
          color: 'white',
          padding: '10px 14px',
          borderRadius: '8px',
          fontSize: '16px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}
      >
        {actual.quality} SHOT
      </Html>

      {/* Technique Assessment - Middle Left */}
      <Html
        position={[-10, 5, 0]}
        distanceFactor={12}
        style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px 14px',
          borderRadius: '6px',
          fontSize: '13px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          minWidth: '160px'
        }}
      >
        <div>
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '6px' }}>TECHNIQUE</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Timing:</span>
            <span style={{ 
              color: actual.timing === 'excellent' ? '#22c55e' : 
                    actual.timing === 'good' ? '#fbbf24' : '#ef4444'
            }}>
              {actual.timing.toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Footwork:</span>
            <span style={{ 
              color: actual.footwork === 'excellent' ? '#22c55e' : 
                    actual.footwork === 'good' ? '#fbbf24' : '#ef4444'
            }}>
              {actual.footwork.toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Balance:</span>
            <span style={{ 
              color: actual.balance === 'excellent' ? '#22c55e' : 
                    actual.balance === 'good' ? '#fbbf24' : '#ef4444'
            }}>
              {actual.balance.toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Follow:</span>
            <span style={{ 
              color: actual.followThrough === 'complete' ? '#22c55e' : 
                    actual.followThrough === 'partial' ? '#fbbf24' : '#ef4444'
            }}>
              {actual.followThrough.toUpperCase()}
            </span>
          </div>
        </div>
      </Html>

      {/* Spin Rate Indicator */}
      {animation.currentPhase === 'postImpact' && (
        <Html
          position={[0, actualTrajectory.maxHeight + 2, 
                   actualTrajectory.points[Math.floor(actualTrajectory.points.length / 2)]?.position.z || 15]}
          distanceFactor={15}
          style={{
            background: 'rgba(59, 130, 246, 0.9)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: 'monospace',
            pointerEvents: 'none',
            textAlign: 'center'
          }}
        >
          <div>
            <div>SPIN RATE</div>
            <div style={{ fontWeight: 'bold' }}>{Math.round(actual.spinRate)} RPM</div>
          </div>
        </Html>
      )}
    </group>
  );
};